import React, { useCallback, useEffect, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { createPortal } from 'react-dom'
import classnames from 'classnames'
import PropTypes from 'prop-types'

import EmojiPicker from '../../EmojiPicker'
import MaxLengthMessage from '../../../features/components/MaxLengthMessage'

import { TabletUp } from '../../hoc/ResponsiveRendering'
import { ReactComponent as SmileIcon } from '../../../assets/icons/smile.svg'

import { useDebounce } from '../../../hooks/useDebounce'
import { clearLSEPCharacters } from '../clearLSEPCharacters'

import useStyles from './styles'

function TextareaField({
  name,
  formikValue,
  maxLength,
  symbol,
  hasEmojiPicker,
  showError,
  setFieldValue,
  setFieldTouched,
  enableReinitialize,
  placeholder,
  portalElement,
  onBlur,
  onKeyPress,
  modifyValueOnBlur,
  disabled,
  className,
  setOnEmptyChange,
  minRows
}) {
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  // We need to clear the value in some cases:
  // (e.g. user copy text with special character LSEP from text editor, and paste it to input)
  const cleanFormikValue = clearLSEPCharacters(formikValue)

  const [currentValue, setCurrentValue] = useState(cleanFormikValue)
  const [valueReinitialized, setValueReinitialized] = useState(false)
  const hasValue = !!currentValue

  const classes = useStyles({ hasValue, showError, hasEmojiPicker })

  const toggleEmojiPicker = useCallback(() => {
    setIsPickerOpen(prevState => !prevState)
  }, [])

  const handleFieldChange = useCallback(
    e => {
      const { value } = e.target
      setCurrentValue(value)

      if (setOnEmptyChange && !value) {
        setFieldValue(name, '')
        setFieldTouched(name, true, false)
      }
    },
    [setFieldValue, name, setOnEmptyChange, setFieldTouched]
  )

  const onEmojiChoose = useCallback(emoji => {
    setCurrentValue(oldValue => oldValue + emoji.native)
  }, [])

  const handleBlur = useCallback(
    e => {
      onBlur && onBlur(e)

      if (modifyValueOnBlur) {
        const modifiedValue = modifyValueOnBlur(e.target.value)
        setCurrentValue(modifiedValue)
      }

      setFieldTouched(name, true, false)
    },
    [setFieldTouched, name, onBlur, modifyValueOnBlur]
  )

  // To improve performance, we update formik only when the user has finished typing
  const debouncedCurrentValue = useDebounce(currentValue, 100)
  useEffect(() => {
    let cleanValue = clearLSEPCharacters(debouncedCurrentValue)
    if (cleanValue !== debouncedCurrentValue) {
      // Set local state value if some character was found
      // We need to clear the value in some cases:
      // (e.g. user copy text with special character LSEP from text editor, and paste it to input)
      setCurrentValue(cleanValue)
    }
    setFieldValue(name, cleanValue)
  }, [debouncedCurrentValue, setFieldValue, name])

  useEffect(() => {
    // we need to enable init value reinitialization for cases like edit form, when value is not available right away
    if (enableReinitialize && !valueReinitialized && cleanFormikValue !== currentValue) {
      setCurrentValue(cleanFormikValue)
      setValueReinitialized(true)
    }
    // eslint-disable-next-line
  }, [cleanFormikValue, enableReinitialize, valueReinitialized])

  return (
    <>
      <div className={classes.wrapper}>
        <div className={classes.textAreaWrapper}>
          <TextareaAutosize
            value={currentValue}
            onBlur={handleBlur}
            onChange={handleFieldChange}
            onKeyPress={onKeyPress}
            className={classnames(classes.textarea, className)}
            maxLength={maxLength}
            placeholder={placeholder}
            disabled={disabled}
            minRows={minRows}
          />

          {hasEmojiPicker ? (
            <TabletUp>
              {
                <span onClick={toggleEmojiPicker} className={classes.icon}>
                  <SmileIcon />
                </span>
              }
            </TabletUp>
          ) : (
            <span className={classes.icon}>{symbol}</span>
          )}
        </div>
        {isPickerOpen && (
          <>
            <br />
            <EmojiPicker style={{ width: '100%' }} onEmojiSelect={onEmojiChoose} />
          </>
        )}
      </div>
      {maxLength &&
        portalElement &&
        createPortal(<MaxLengthMessage maxLength={maxLength} length={currentValue.length} />, portalElement)}
    </>
  )
}

TextareaField.propTypes = {
  name: PropTypes.string,
  formikValue: PropTypes.string,
  maxLength: PropTypes.number,
  symbol: PropTypes.node,
  hasEmojiPicker: PropTypes.bool,
  showError: PropTypes.bool,
  setFieldValue: PropTypes.func,
  setFieldTouched: PropTypes.func,
  enableReinitialize: PropTypes.bool,
  placeholder: PropTypes.string,
  portalElement: PropTypes.object,
  onBlur: PropTypes.func,
  onKeyPress: PropTypes.func,
  modifyValueOnBlur: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  minRows: PropTypes.number
}

export default TextareaField
